import styled from "styled-components";
import {Layout, Content, Eyebrow, Lede, OfferDialog} from "upsell/ExitOfferDialog.styles";

/** The shared layout can be injected later by another dialog; these overrides must not depend on CSS order. */
export const ClaimLayout = styled(Layout)<{$regular: boolean}>`
	&& {
		display: grid;
		grid-template-columns: ${({$regular}) => $regular ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)"};
		/* Preserve full row content and padding when the dialog is shorter than its contents. */
		grid-auto-rows: max-content;
		overflow-y: auto;
		@media (max-width: 640px) { grid-template-columns: minmax(0, 1fr); }
	}
`;

/**
 * The claim dialog's paper. `$regular` is the regular-price state, which has no ticket
 * (kitecorp/transferify-api-service#2206): the paper narrows so the plan copy is not stretched across the
 * two-column card's 780px beside an empty half. A prop rather than a second component, so a coupon that
 * turns into the regular price on screen keeps its dialog instead of remounting it.
 */
export const ClaimDialog = styled(OfferDialog)<{$regular: boolean}>`
	& .MuiPaper-root { ${({$regular}) => $regular ? "width: 460px;" : ""} }
`;

/**
 * The counter the ticket lies on: a very pale red (`ticketPanel`, owner 2026-09-09), after a
 * near-black and then a cream version. The ticket's shadow follows its mask, so the notches and the
 * seam read as cut paper on it.
 *
 * The ticket sits at the TOP of its column, level with the plan copy opposite, never vertically
 * centred: once Subscribe swaps the plan copy for the embedded checkout the column grows to the
 * sheet's height and a centred ticket slid to the bottom of the dialog (owner 2026-09-09).
 */
export const TicketColumn = styled.div`
	min-width: 0;
	display: grid;
	align-items: start;
	/* The column fills the row (owner 2026-09-09: the panel stopped short of the dialog's bottom) and
	   keeps the ticket at its top, so the panel grows with the card sheet while the ticket stays put. */
	align-content: start;
	align-self: stretch;
	justify-items: center;
	padding: 58px 14px 30px;
	/* Narrower than the success card's 350px ticket (owner 2026-09-09: "too wide" at the column's
	   full 362px); the paper keeps its print at this measure and the panel shows around it. */
	> * { width: min(100%, 300px); }
	background: ${({theme}) => theme.tokens.ticketPanel};
`;

/** Only ClaimLayout scrolls. The close control is outside it on the dialog paper. */
export const ClaimContent = styled(Content)`
	&& {
		overflow: visible;
		padding-top: 58px;
		@media (max-width: 640px) { padding-top: 22px; }
	}
`;

/**
 * Secondary accent, not the shared eyebrow's orange or the plain ink it wore first (owner,
 * 2026-09-09). `signalInk`, not `teal`: the accent as INK — raw teal measures 2.84:1 on the white
 * column and failed this dialog's own Axe gate at 12px.
 */
export const ClaimEyebrow = styled(Eyebrow)`
	&& { font-weight: 800; color: ${({theme}) => theme.tokens.signalInk}; }
`;

export const ClaimBenefit = styled(Lede)`
	&& { margin-bottom: 6px; }
`;

/**
 * A live subscriber's Terms line, under the express request (kitecorp/transferify-ui#3457). The consent's root
 * carries a top margin and a rule but no bottom margin (`upsell/ExitOfferDialog.styles.Continue` says why), so
 * the line owns the space above itself or the sentence's last line touches it. 6px, the gap the plan-change
 * dialog's `OrderNoteSlot` puts between the same two lines, so the two subscriber dialogs read alike.
 */
export const ClaimTerms = styled.div`
	margin: 6px 0 0;
`;
